import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../features/apiServices/apiClient";
import "./css/MenusPage.css";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import ConfirmModal from "../components/ConfirmModal";
import safeJSON from "../components/safeJSON";


const MenusPage = () => {

  const navigate = useNavigate();
  const mesaLogo = "/assets/logo-mesaclick.png"
  const restaurante = safeJSON("activeRestaurant");
  const tenant = safeJSON("tenant");
  const token = localStorage.getItem("accessToken");

  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    preco: "",
    categoria: "",
    foto: "",
    restaurant_id: restaurante?.id,
  });

  const [preview, setPreview] = useState(null);
  const [menus, setMenus] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState("");
  const fileInputRef = useRef(null);

  // Modal de sucesso
  const [modalMessage, setModalMessage] = useState("");

  // Modal delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);


  useEffect(() => {
    if (!localStorage.getItem("accessToken")) return;
    
    loadMenus();
  }, []);

  const loadMenus = async () => {
    try {
      const response = await api.get(`/menu/restaurant_id/${restaurante.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-tenant-slug": tenant.slug,
        },
      });
      setMenus(response.data);
    } catch (err) {
      console.error("Erro ao carregar menus:", err);
    }
  };

  const openModal = (msg) => {
    setModalMessage(msg);
    setTimeout(() => setModalMessage(""), 2500);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setConfirmDelete(true);
  };


  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1];
      setFormData({ ...formData, foto: base64 });
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      descricao: "",
      preco: "",
      categoria: "",
      foto: "",
      restaurant_id: restaurante.id,
    });
    setPreview(null);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await api.put(
          `/menu/${editingId}`,
          {
            ...formData,
            preco: Number(formData.preco),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-tenant-slug": tenant.slug,
            },
          }
        );
        openModal("Menu atualizado com sucesso!");
      } else {
        await api.post(
          "/menu",
          {
            ...formData,
            preco: Number(formData.preco),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-tenant-slug": tenant.slug,
            },
          }
        );

        openModal("Menu cadastrado com sucesso!");
      }

      resetForm();
      loadMenus();
    } catch (err) {
      console.error("Erro ao salvar menu:", err);
      openModal("Erro ao salvar menu.");
    }
  };

  const handleEdit = (menu) => {
    setFormData(menu);
    setPreview(`data:image/png;base64,${menu.foto}`);
    setEditingId(menu.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/menu/${itemToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "x-tenant-slug": tenant.slug,
        },
      });

      setConfirmDelete(false);
      setItemToDelete(null);

      openModal("Item excluído com sucesso!");
      loadMenus();
    } catch (err) {
      console.error("Erro ao excluir menu:", err);
      openModal("Erro ao excluir item.");
    }
  };

  return (
    <div className="menus-container">

      {/* HEADER */}
      <header className="menus-header">
        <img
          src={mesaLogo}
          alt="MesaClick"
          className="menus-logo"
          onClick={() => navigate("/home")}
        />
        <h1 className="header-title">Menus</h1>

        {restaurante && (
          <span className="mc-rest-badge" style={{ marginLeft: "auto" }}>
            <span className="mc-rest-badge-dot" />
            {restaurante.nome}
          </span>
        )}
      </header>

      {/* MODAL */}
      {modalMessage && (
        <div className="mesa-modal-overlay">
          <div className="mesa-modal">
            <img src={mesaLogo} className="mesa-modal-logo" />
            <p>{modalMessage}</p>
            <button onClick={() => setModalMessage("")}>Ok</button>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />


      <div className="menus-content">

        {/* FORM */}
        <div className="menus-card">
          <h2 className="menus-section-title">Cadastro de Pratos</h2>

          <form onSubmit={handleSubmit} className="menus-form">

            <label>Nome do prato</label>
            <input name="nome" value={formData.nome} onChange={handleInputChange} />

            <label>Descrição</label>
            <textarea name="descricao" value={formData.descricao} onChange={handleInputChange} />

            <label>Preço</label>
            <input name="preco" type="number" value={formData.preco} placeholder="0.00" onChange={handleInputChange} />

            <label>Categoria</label>
            <input name="categoria" value={formData.categoria} onChange={handleInputChange} />

            <label>Foto do prato</label>
            <input type="file" ref={fileInputRef} onChange={handleImageUpload} />

            <button type="submit" className="menus-submit">
              {editingId ? "Atualizar Menu" : "Cadastrar Menu"}
            </button>
          </form>
        </div>

        {/* PREVIEW */}
        <div className="menus-card preview-card">
          <h3>Pré-visualização</h3>
          {preview ? (
            <img src={preview} className="menus-preview" />
          ) : (
            <div className="menus-preview-empty">Nenhuma imagem selecionada</div>
          )}
        </div>

      </div>

      {/* LISTAGEM */}
      <div className="menus-list-card">
        <h2 className="menus-section-title">Itens Cadastrados</h2>

        {/* FILTRO */}
        <div className="menus-filter">
          <label>Filtrar por categoria:</label>
          <select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
            <option value="">Todas</option>
            {[...new Set(menus.map((m) => m.categoria))].map((cat) => (
              <option key={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <table className="menus-table">
          <thead>
            <tr>
              <th>Foto</th>
              <th>Descrição</th>
              <th>Categoria</th>
              <th>Preço</th>
              <th>Ações</th>
            </tr>
          </thead>

          <tbody>
            {menus
              .filter((m) => !filtroCategoria || m.categoria === filtroCategoria)
              .map((menu) => (
                <tr key={menu.id}>
                  <td>
                    <img
                      src={`data:image/png;base64,${menu.foto}`}
                      className="menus-thumb"
                    />
                  </td>
                  <td>{menu.nome}</td>
                  <td>{menu.categoria}</td>
                  <td>R$ {(menu.preco).toFixed(2)}</td>
                  <td className="menus-actions">
                    <PencilSquareIcon onClick={() => handleEdit(menu)} className="menus-icon edit" />
                    <TrashIcon onClick={() => handleDeleteClick(menu.id)} className="menus-icon delete" />
                  </td>
                </tr>
              ))}

            {menus.length === 0 && (
              <tr>
                <td colSpan="5" className="menus-empty">Nenhum item cadastrado.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MenusPage;
